import { Button } from '@progress/kendo-react-buttons';
import { Breadcrumb } from '@progress/kendo-react-layout';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { hideLoading, showLoading } from '../../actions/api';
import { showNotification } from '../../actions/notifications';
import { DocumentKind, StorageItem, StorageItemFull } from '../../constants/document';
import NotificationsTypes from '../../constants/notification';
import documentsService from '../../services/documentsService';
import { forceDownload, getDocumentKindByContentType } from '../../utils';
import DocumentViewerSkeleton from '../loading/DocumentViewerSkeleton';
import styles from './DocumentsViewer.module.css';

interface BreadcrumbData {
  id: string;
  text: string;
  iconClass?: string;
}

const buildBreadcrumbData = (path: string | undefined): BreadcrumbData[] => {
  if (!path) return [];
  const segments = path.split('/').filter(Boolean);
  const lastIndex = segments.length - 1;
  return segments.map((segment, idx) => ({
    id: String(idx),
    text: segment,
    iconClass: idx === 0 ? 'k-i-home' : idx === lastIndex ? undefined : 'k-i-folder',
  }));
};

const DocumentsViewer = (item: StorageItemFull) => {
  const dispatch = useDispatch();
  const [storageFullItem, setStorageFullItem] = useState<StorageItemFull | undefined>(undefined);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.path]);

  const breadcrumbData = useMemo(
    () => buildBreadcrumbData(storageFullItem?.path),
    [storageFullItem?.path],
  );

  const getData = async () => {
    try {
      // No global spinner here — DocumentViewerSkeleton renders in place
      // while storageFullItem is undefined, so the contextual placeholder
      // is the loading feedback. Errors still surface via showNotification.
      if (item.url) {
        setStorageFullItem(item);
      } else {
        const response = await documentsService.getDocumentInfo(item.path);
        if (response) {
          setStorageFullItem(response);
        }
      }
    } catch (error: any) {
      setStorageFullItem(undefined);
      dispatch(
        showNotification({
          message: 'Retrieve document failed, try again later or contact the support team',
          type: NotificationsTypes.Error,
        }),
      );
    }
  };

  const getDefaultRender = () => {
    if (storageFullItem)
      switch (getDocumentKindByContentType(storageFullItem.contentType)) {
        case DocumentKind.image:
          return getPreviewImage();
        case DocumentKind.pdf:
          return getPreviewDocument();
        default:
          return getPreviewNotAvailable();
      }
  };

  const renderHeader = (onDownload: () => void) => (
    <div className={styles['viewer-header']}>
      <Breadcrumb
        data={breadcrumbData}
        className={styles['viewer-breadcrumb']}
        textField="text"
        iconClassField="iconClass"
        ariaLabel="Document path"
      />
      <Button
        icon="download"
        className="btn-action-add"
        title="Download file"
        onClick={onDownload}
      />
    </div>
  );

  const getPreviewDocument = () => (
    <>
      {renderHeader(() => forceDownload(storageFullItem?.url ?? '', storageFullItem?.text ?? ''))}
      <div className={styles['viewer-content']}>
        <iframe
          src={storageFullItem?.url}
          title={storageFullItem?.text}
          style={{ height: '100%', width: '100%', border: 'none' }}
        />
      </div>
    </>
  );

  const getPreviewImage = () => (
    <>
      {renderHeader(() => forceDownload(storageFullItem?.url ?? '', storageFullItem?.text ?? ''))}
      <div className={styles['viewer-content']}>
        <img
          src={storageFullItem?.url}
          alt={storageFullItem?.text}
          style={{ maxHeight: '100%', maxWidth: '100%' }}
        />
      </div>
    </>
  );

  const getPreviewNotAvailable = () => (
    <>
      {renderHeader(() => onDownloadDocument(storageFullItem))}
      <div className={styles['viewer-content']}>
        <h3>Preview Not Available</h3>
      </div>
    </>
  );

  const onDownloadDocument = async (item: StorageItem | undefined) => {
    try {
      dispatch(showLoading());
      window.open(item?.url);
    } catch (error: any) {
      dispatch(showNotification({ message: `Document not available.`, type: NotificationsTypes.Error }));
    } finally {
      dispatch(hideLoading());
    }
  };

  return (
    <div className={styles['viewer-wrapper']}>
      {!storageFullItem ? <DocumentViewerSkeleton /> : getDefaultRender()}
    </div>
  );
};

export default DocumentsViewer;
